<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bell, BellOff, BellRing, ChevronRight, Circle, ExternalLink, MoreHorizontal, X } from 'lucide-vue-next';
import { formatRelativeDate } from '~/lib/dateFormat';
import { notificationAccentClass } from '~/lib/notifications';
import type { AppNotification, NotificationSurface } from '~/types/models';

const props = withDefaults(defineProps<{
    surface: NotificationSurface;
    tenantId?: string | null;
    clientId?: string | null;
    showHeader?: boolean;
    title?: string;
    description?: string;
    emptyMessage?: string;
}>(), {
    showHeader: true,
    title: '',
    description: '',
    emptyMessage: 'You are all caught up.',
});

const emit = defineEmits<{
    (e: 'select', notification: AppNotification): void;
}>();

const notificationStore = useNotificationStore();

const context = computed(() => ({
    surface: props.surface,
    tenantId: props.tenantId,
    clientId: props.clientId,
}));
const notifications = computed(() => notificationStore.visibleNotifications(context.value));
const unreadCount = computed(() => notificationStore.unreadCountForContext(context.value));
const feedTitle = computed(() => props.title || (props.surface === 'tenant' ? 'Workspace Notifications' : 'Portal Notifications'));
const feedDescription = computed(() => props.description || (
    props.surface === 'tenant'
        ? 'Important updates that deserve attention without turning the app into noise.'
        : 'Important client-side updates from messages, approvals, tasks, and billing.'
));

async function handleNotificationClick(item: AppNotification) {
    await notificationStore.markAsRead(item.id);
    emit('select', item);
}

async function markAllAsRead() {
    await notificationStore.markAllAsRead(context.value);
}

async function dismissAll() {
    await notificationStore.dismissAll(context.value);
}

async function dismissNotification(item: AppNotification) {
    await notificationStore.dismissNotification(item.id);
}

async function muteClient(item: AppNotification) {
    if (!item.tenantId || !item.clientId) return;
    await notificationStore.setClientMuted(item.tenantId, item.clientId, true, item.title);
}

async function muteItem(item: AppNotification) {
    if (!item.tenantId || !item.entityType || !item.entityId) return;
    await notificationStore.setEntityMuted(item.tenantId, item.entityType, item.entityId, true, {
        clientId: item.clientId,
        projectId: item.projectId,
        label: item.title,
    });
}
</script>

<template>
    <div class="space-y-4">
        <div v-if="showHeader" class="space-y-3">
            <div class="flex items-start gap-3">
                <div class="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <BellRing class="size-4.5" />
                </div>
                <div class="min-w-0 flex-1 space-y-2">
                    <div class="flex flex-wrap items-center gap-2">
                        <h2 class="text-lg font-semibold text-foreground">{{ feedTitle }}</h2>
                        <Badge variant="outline">{{ unreadCount }} unread</Badge>
                    </div>
                    <p class="text-sm text-muted-foreground">{{ feedDescription }}</p>
                </div>
            </div>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3">
            <p class="text-xs text-muted-foreground">Clicking an item marks it read and takes you to the relevant action.</p>
            <div class="flex items-center gap-1">
                <Button variant="ghost" size="sm" :disabled="unreadCount === 0" @click="markAllAsRead">
                    Mark all read
                </Button>
                <Button variant="ghost" size="sm" :disabled="notifications.length === 0" @click="dismissAll">
                    Dismiss all
                </Button>
            </div>
        </div>

        <Card
            v-if="notificationStore.loading && notifications.length === 0"
            class="min-h-40 items-center justify-center px-4 text-sm text-muted-foreground"
        >
            Loading notifications…
        </Card>

        <EmptyState
            v-else-if="notifications.length === 0"
            :title="emptyMessage"
            description=""
            class="min-h-40"
        >
            <template #icon>
                <Bell class="size-5" />
            </template>
        </EmptyState>

        <div v-else class="space-y-3">
            <div
                v-for="item in notifications"
                :key="item.id"
                :class="[
                    'group flex w-full items-start gap-1 rounded-2xl border px-2 py-2 transition hover:border-primary/30 hover:bg-accent/30',
                    notificationAccentClass(item),
                    !item.readAt ? 'shadow-[0_1px_1px_rgba(15,23,42,0.012)]' : '',
                ]"
            >
                <button type="button" class="min-w-0 flex-1 rounded-xl px-2 py-2 text-left" @click="handleNotificationClick(item)">
                    <div class="flex items-start gap-3">
                        <Circle v-if="!item.readAt" class="mt-1 size-2.5 fill-current text-primary" />
                        <span v-else class="mt-1 block h-2.5 w-2.5 shrink-0 rounded-full bg-transparent" />
                        <div class="min-w-0 flex-1 space-y-2">
                            <div class="flex items-start justify-between gap-3">
                                <div class="min-w-0">
                                    <p class="truncate text-sm font-semibold text-foreground">{{ item.title }}</p>
                                    <p class="mt-1 line-clamp-2 text-sm leading-5 text-muted-foreground">{{ item.body }}</p>
                                </div>
                                <span class="shrink-0 text-xs text-muted-foreground">{{ formatRelativeDate(item.createdAt) }}</span>
                            </div>

                            <div class="flex flex-wrap items-center gap-2">
                                <Badge v-if="item.severity === 'critical'" variant="destructive">Critical</Badge>
                                <Badge v-else-if="item.severity === 'warning'" variant="outline" class="border-amber-400/60 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                                    Needs attention
                                </Badge>
                                <Badge variant="outline">{{ item.surface === 'tenant' ? 'Workspace' : 'Portal' }}</Badge>
                                <span
                                    v-if="item.actionLabel || item.actionPath"
                                    class="inline-flex items-center gap-1 text-xs font-medium text-primary"
                                >
                                    {{ item.actionLabel || 'Open' }}
                                    <ExternalLink class="size-3" />
                                </span>
                            </div>
                        </div>
                        <ChevronRight class="mt-1 size-4 shrink-0 text-muted-foreground transition group-hover:text-foreground" />
                    </div>
                </button>

                <div class="flex shrink-0 items-center gap-0.5 pt-1">
                    <Button variant="ghost" size="icon" class="size-8" @click="dismissNotification(item)">
                        <X class="size-4" />
                        <span class="sr-only">Dismiss notification</span>
                    </Button>
                    <DropdownMenu v-if="item.clientId || (item.entityType && item.entityId)">
                        <DropdownMenuTrigger as-child>
                            <Button variant="ghost" size="icon" class="size-8">
                                <MoreHorizontal class="size-4" />
                                <span class="sr-only">Notification options</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" class="w-52">
                            <DropdownMenuItem v-if="item.entityType && item.entityId" @select="muteItem(item)">
                                <BellOff class="mr-2 size-4" />
                                Mute this item
                            </DropdownMenuItem>
                            <DropdownMenuSeparator v-if="item.clientId && item.entityType && item.entityId" />
                            <DropdownMenuItem v-if="item.clientId" @select="muteClient(item)">
                                <BellOff class="mr-2 size-4" />
                                Mute this client
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </div>
    </div>
</template>
